import { useState } from "react";
import { api } from "@/lib/api";
import { Plus, X as XIcon } from "@phosphor-icons/react";

const formatNN = (n) => String(n).padStart(2, "0");

/**
 * Barra de pestañas superior para cambiar entre encargos (01, 02, 03...).
 * El encargo 01 es el principal (los datos del expediente). Los 02+ viven
 * en `exp.encargos_adicionales`.
 */
export default function EncargosTabBar({ exp, active, onChange, onReload }) {
  const adicionales = (exp.encargos_adicionales || [])
    .slice()
    .sort((a, b) => (a.numero || 0) - (b.numero || 0));

  const [creating, setCreating] = useState(false);
  const [draftIndicaciones, setDraftIndicaciones] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const nextNum = (adicionales.length ? Math.max(...adicionales.map((e) => e.numero || 1)) : 1) + 1;

  const create = async () => {
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post(`/expedientes/${exp.id}/encargos`, {
        indicaciones: draftIndicaciones.trim() || null,
      });
      setCreating(false);
      setDraftIndicaciones("");
      await onReload?.();
      // Cambiar automáticamente al encargo recién creado
      onChange?.(data.encargo.numero);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error creando encargo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F3F4F6] border-b border-border px-5 pt-3 relative" data-testid="encargos-tab-bar">
      <div className="flex items-end gap-1">
        {/* Tab 01 (principal) */}
        <TabButton num={1} active={active === 1} onClick={() => onChange(1)} testid="encargo-tab-1" />

        {/* Tabs adicionales */}
        {adicionales.map((enc) => (
          <TabButton
            key={enc.id}
            num={enc.numero}
            active={active === enc.numero}
            onClick={() => onChange(enc.numero)}
            testid={`encargo-tab-${enc.numero}`}
          />
        ))}

        {/* Botón + NUEVO */}
        <button
          onClick={() => setCreating(true)}
          data-testid="add-encargo-button"
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-t-md border-2 border-b-0 border-brand/40 bg-brand-soft/40 text-brand font-bold text-[11px] uppercase tracking-wider hover:bg-brand-soft transition-colors self-end"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" />
          Nuevo
        </button>
      </div>

      {/* Modal inline de creación */}
      {creating && (
        <div className="absolute top-full left-5 right-5 mt-0 z-10 rounded-b-lg border border-t-0 border-brand/40 bg-white shadow-xl p-4" data-testid="new-encargo-form">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-[13px] tracking-tight text-foreground">
              Nuevo encargo {formatNN(nextNum)}
            </div>
            <button
              onClick={() => { setCreating(false); setDraftIndicaciones(""); setError(""); }}
              className="p-1 text-muted hover:text-foreground"
            >
              <XIcon className="w-3.5 h-3.5" weight="bold" />
            </button>
          </div>
          <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1 block">
            Indicaciones del encargo {formatNN(nextNum)}
          </label>
          <textarea
            rows={3}
            value={draftIndicaciones}
            onChange={(e) => setDraftIndicaciones(e.target.value)}
            data-testid="new-encargo-indicaciones"
            placeholder="Ej. Revisita para comprobar el secado de paredes tras dos semanas."
            className="!text-[12px] !bg-white"
            autoFocus
          />
          {error && <div className="text-[11px] text-status-error-fg mt-1">{error}</div>}
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setCreating(false); setDraftIndicaciones(""); setError(""); }}
              className="text-[11px] px-3 py-1.5 rounded-md border border-border bg-white hover:bg-surface font-medium"
            >Cancelar</button>
            <button
              onClick={create}
              disabled={submitting}
              data-testid="submit-encargo-button"
              className="text-[11px] px-3 py-1.5 rounded-md bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-50"
            >
              {submitting ? "Creando..." : `Crear encargo ${formatNN(nextNum)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ num, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative px-5 py-2.5 rounded-t-md font-display font-bold text-[15px] tracking-tight transition-all self-end ${
        active
          ? "bg-white text-brand border border-b-0 border-border shadow-sm translate-y-px z-10"
          : "bg-brand text-white hover:bg-brand-hover"
      }`}
    >
      <div className="flex flex-col items-center leading-none">
        <span>{formatNN(num)}</span>
        <span className={`text-[9px] uppercase tracking-widest font-medium mt-0.5 ${
          active ? "text-muted" : "text-white/80"
        }`}>
          encargo
        </span>
      </div>
    </button>
  );
}
